import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useAppConfig } from '../context/AppConfigContext';
import { chatAPI } from '../services/api';
import ConfirmModal from '../components/common/ConfirmModal';
import {
  Send,
  Bot,
  User,
  MessageSquare,
  LogOut,
  Settings,
  ChevronDown,
  Loader,
  FileText,
  FolderOpen,
  Check
} from 'lucide-react';

function ChatPage() {
  const { user, projects, selectedProjectId, selectedProject, selectProject, logout, isAdmin, refreshProjects } = useAuth();
  const { appName } = useAppConfig();
  const navigate = useNavigate();

  const [messages, setMessages] = useState([]);
  const [inputValue, setInputValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [showProjectSelector, setShowProjectSelector] = useState(false);
  const [logoutModalOpen, setLogoutModalOpen] = useState(false);
  const [changeProjectModal, setChangeProjectModal] = useState({ open: false, projectId: null, projectName: '' });

  const messagesEndRef = useRef(null);
  const textareaRef = useRef(null);

  // Refresh projects on mount (picks up new assignments without re-login)
  useEffect(() => {
    refreshProjects();
  }, []);

  // Fetch documents when project changes
  useEffect(() => {
    if (selectedProjectId) {
      fetchDocuments();
      setMessages([]);
    }
  }, [selectedProjectId]);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [inputValue]);

  const fetchDocuments = async () => {
    try {
      const response = await chatAPI.getKnowledgeStatus(selectedProjectId);
      setDocuments(response.data.documents || []);
    } catch (error) {
      console.error('Error fetching documents:', error);
    }
  };

  const handleSendMessage = async () => {
    if (!inputValue.trim() || isLoading || !selectedProjectId) return;

    const userMessage = inputValue.trim();
    setInputValue('');

    const newUserMessage = {
      id: Date.now(),
      role: 'user',
      content: userMessage,
    };

    setMessages((prev) => [...prev, newUserMessage]);
    setIsLoading(true);

    try {
      const conversationHistory = messages.slice(-10).map((msg) => ({
        role: msg.role,
        content: msg.content,
      }));

      const response = await chatAPI.sendMessage(userMessage, selectedProjectId, conversationHistory);

      const assistantMessage = {
        id: Date.now() + 1,
        role: 'assistant',
        content: response.data.response,
        sources: response.data.sources || [],
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (error) {
      setMessages((prev) => [
        ...prev,
        {
          id: Date.now() + 1,
          role: 'assistant',
          content: 'Lo siento, hubo un error al procesar tu mensaje. Por favor, intenta de nuevo.',
        },
      ]);
    } finally {
      setIsLoading(false);
      // Mantener foco en el textbox después de la respuesta
      setTimeout(() => textareaRef.current?.focus(), 0);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleLogout = () => {
    setLogoutModalOpen(true);
  };

  const confirmLogout = () => {
    setLogoutModalOpen(false);
    logout();
    navigate('/login');
  };

  const handleProjectChange = (projectId, projectName) => {
    setShowProjectSelector(false);
    // Si hay mensajes, pedir confirmación
    if (messages.length > 0) {
      setChangeProjectModal({ open: true, projectId, projectName });
    } else {
      selectProject(projectId);
    }
  };

  const confirmProjectChange = () => {
    selectProject(changeProjectModal.projectId);
    setChangeProjectModal({ open: false, projectId: null, projectName: '' });
  };

  const suggestions = [
    '¿Qué información hay disponible?',
    '¿Puedes resumir los documentos?',
    '¿Cuáles son los puntos principales?',
  ];

  if (!selectedProjectId && projects.length > 0) {
    return (
      <div className="chat-page">
        <div className="project-selector-page">
          <div className="project-selector-card">
            <img src="/watson-logo.svg" alt={appName} className="selector-logo" />
            <h2>Selecciona un Proyecto</h2>
            <p>Elige el proyecto con el que deseas trabajar</p>
            <div className="project-list">
              {projects.map((project) => (
                <button
                  key={project.id}
                  className="project-item-button"
                  onClick={() => selectProject(project.id.toString())}
                >
                  {project.name}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (projects.length === 0) {
    return (
      <div className="chat-page">
        <div className="project-selector-page">
          <div className="project-selector-card">
            <img src="/watson-logo.svg" alt={appName} className="selector-logo" />
            <h2>Sin Proyectos</h2>
            <p>No tienes proyectos asignados. Contacta al administrador.</p>
            <button className="logout-button-standalone" onClick={handleLogout}>
              <LogOut size={20} />
              Cerrar Sesión
            </button>
          </div>
        </div>

        {/* Logout Confirmation Modal */}
        <ConfirmModal
          isOpen={logoutModalOpen}
          title="Cerrar Sesión"
          message="¿Estás seguro de que deseas cerrar sesión?"
          confirmText="Cerrar Sesión"
          onConfirm={confirmLogout}
          onCancel={() => setLogoutModalOpen(false)}
          danger={false}
        />
      </div>
    );
  }

  return (
    <div className="chat-page">
      <div className="chat-container">
      <header className="chat-header">
        <div className="chat-header-left">
          <h1>
            <img src="/watson-logo.svg" alt={appName} className="header-logo" />
            {appName}
          </h1>
        </div>

        <div className="chat-header-center">
          <div className="project-selector-dropdown">
            <button
              className="project-selector-button"
              onClick={() => setShowProjectSelector(!showProjectSelector)}
            >
              <div className="project-selector-info">
                <span className="project-selector-label">Proyecto</span>
                <span className="project-selector-name">{selectedProject?.name || 'Seleccionar'}</span>
              </div>
              <ChevronDown size={16} className={`chevron-icon ${showProjectSelector ? 'open' : ''}`} />
            </button>
            {showProjectSelector && (
              <>
                <div className="dropdown-backdrop" onClick={() => setShowProjectSelector(false)} />
                <div className="project-dropdown-menu">
                  <div className="dropdown-header">Cambiar proyecto</div>
                  {projects.map((project) => (
                    <button
                      key={project.id}
                      className={`project-dropdown-item ${
                        project.id.toString() === selectedProjectId ? 'active' : ''
                      }`}
                      onClick={() => handleProjectChange(project.id.toString(), project.name)}
                      disabled={project.id.toString() === selectedProjectId}
                    >
                      <FolderOpen size={16} />
                      <span className="project-item-name">{project.name}</span>
                      {project.id.toString() === selectedProjectId && (
                        <Check size={16} className="check-icon" />
                      )}
                    </button>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>

        <div className="chat-header-right">
          <div className="chat-status">
            <span className={`status-dot ${documents.length > 0 ? '' : 'offline'}`}></span>
            {documents.length > 0 ? `${documents.length} doc(s)` : 'Sin docs'}
          </div>
          <span className="user-info">
            <User size={16} />
            {user?.username}
          </span>
          {isAdmin && (
            <button className="icon-button" onClick={() => navigate('/admin')} title="Panel Admin">
              <Settings size={20} />
            </button>
          )}
          <button className="icon-button" onClick={handleLogout} title="Cerrar sesión">
            <LogOut size={20} />
          </button>
        </div>
      </header>

      <div className="messages-container">
        {messages.length === 0 ? (
          <div className="welcome-message">
            <div className="welcome-icon">
              <img src="/watson-logo.svg" alt={appName} className="welcome-logo" />
            </div>
            <h2>¡Hola! Soy {appName}</h2>
            <p>
              Puedo responder preguntas basándome en los documentos del proyecto{' '}
              <strong>{selectedProject?.name}</strong>. ¿En qué puedo ayudarte hoy?
            </p>
            <div className="suggestion-chips">
              {suggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="suggestion-chip"
                  onClick={() => {
                    setInputValue(suggestion);
                    textareaRef.current?.focus();
                  }}
                >
                  {suggestion}
                </button>
              ))}
            </div>
          </div>
        ) : (
          messages.map((message) => (
            <div key={message.id} className={`message ${message.role}`}>
              <div className="message-avatar">
                {message.role === 'assistant' ? (
                  <img src="/watson-logo.svg" alt={appName} className="avatar-logo" />
                ) : (
                  <User size={20} />
                )}
              </div>
              <div className="message-content">
                <div className="message-text">{message.content}</div>
                {message.sources && message.sources.length > 0 && (
                  <details className="message-sources-accordion">
                    <summary className="message-sources-title">
                      Fuentes consultadas ({message.sources.length})
                    </summary>
                    <div className="message-sources-content">
                      {message.sources.map((source, idx) => (
                        <span key={idx} className="source-tag">
                          <FileText size={14} />
                          {source.documentName}
                          {source.areaName && <small> ({source.areaName})</small>}
                        </span>
                      ))}
                    </div>
                  </details>
                )}
              </div>
            </div>
          ))
        )}

        {isLoading && (
          <div className="message assistant">
            <div className="message-avatar">
              <img src="/watson-logo.svg" alt={appName} className="avatar-logo" />
            </div>
            <div className="message-content">
              <div className="typing-indicator">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className="input-area">
        <div className="input-container">
          <div className="input-wrapper">
            <textarea
              ref={textareaRef}
              className="message-input"
              placeholder="Escribe tu pregunta aquí..."
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              rows={1}
              disabled={isLoading}
            />
          </div>
          <button
            className="send-button"
            onClick={handleSendMessage}
            disabled={!inputValue.trim() || isLoading}
          >
            <Send size={20} />
          </button>
        </div>
      </div>
      </div>

      {/* Change Project Confirmation Modal */}
      <ConfirmModal
        isOpen={changeProjectModal.open}
        title="Cambiar de Proyecto"
        message={`Al cambiar al proyecto "${changeProjectModal.projectName}" se perderá la conversación actual. ¿Deseas continuar?`}
        confirmText="Cambiar"
        onConfirm={confirmProjectChange}
        onCancel={() => setChangeProjectModal({ open: false, projectId: null, projectName: '' })}
        danger={false}
      />

      {/* Logout Confirmation Modal */}
      <ConfirmModal
        isOpen={logoutModalOpen}
        title="Cerrar Sesión"
        message="¿Estás seguro de que deseas cerrar sesión?"
        confirmText="Cerrar Sesión"
        onConfirm={confirmLogout}
        onCancel={() => setLogoutModalOpen(false)}
        danger={false}
      />
    </div>
  );
}

export default ChatPage;
